import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

// ФИЛЬТР ТУРОВ.
//
// Туров у компании немного, поэтому городить фасетный фильтр с ценами,
// длительностью и чекбоксами было бы враньём: половина полей стояла бы пустой.
// Здесь ровно два инструмента, которые реально нужны на этом каталоге:
//
//   1) поиск по строке — название, город, маршрут и категория в одном поле;
//   2) переключатель формата — «с датами» (группа уже набирается) против
//      «по запросу» (маршрут собирается под вас).
//
// Фильтрация идёт по ВСЕМ языкам записи, а не только по текущему: человек,
// читающий сайт по-русски, вполне может искать «Kyoto» или «Осака».

const haystack = (activity) => {
  const parts = [];
  const collect = (value) => {
    if (!value) return;
    if (typeof value === "string") parts.push(value);
    else if (typeof value === "object") Object.values(value).forEach(collect);
  };
  collect(activity?.name);
  collect(activity?.place);
  collect(activity?.category);
  collect(activity?.route);
  collect(activity?.text);
  return parts.join(" ").toLowerCase();
};

/** Отбор туров по строке поиска и формату. Возвращает список и состояние. */
export const useTourFilters = (tours) => {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("all"); // all | dated | request

  const list = useMemo(() => (Array.isArray(tours) ? tours : []), [tours]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return list.filter((tour) => {
      if (mode === "dated" && !tour?.date) return false;
      if (mode === "request" && tour?.date) return false;
      if (!needle) return true;
      return haystack(tour).includes(needle);
    });
  }, [list, query, mode]);

  return {
    query,
    setQuery,
    mode,
    setMode,
    filtered,
    total: list.length,
    active: Boolean(query.trim()) || mode !== "all",
    reset: () => {
      setQuery("");
      setMode("all");
    },
  };
};

const MODES = [
  { id: "all", key: "filters.all" },
  { id: "dated", key: "filters.dated" },
  { id: "request", key: "filters.onRequest" },
];

export default function TourFilters({ state, className = "" }) {
  const { t } = useTranslation();

  return (
    <section
      className={`rounded-2xl border border-kj-line bg-kj-card/40 p-4 sm:p-5 ${className}`}
      aria-label={t("filters.title")}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {/* Поиск. Иконка внутри поля, крестик появляется только когда есть что
            стирать — пустая кнопка «очистить» сбивает с толку. */}
        <div className="relative w-full lg:max-w-md">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-kj-sub"
          />
          <input
            type="search"
            value={state.query}
            onChange={(e) => state.setQuery(e.target.value)}
            placeholder={t("filters.search")}
            aria-label={t("filters.search")}
            className="w-full min-h-[44px] rounded-lg border border-kj-line bg-kj-dark/40 pl-9 pr-9 py-2 text-sm text-kj-ink placeholder:text-kj-sub focus:border-kj-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold/50 [&::-webkit-search-cancel-button]:hidden"
          />
          {state.query && (
            <button
              type="button"
              onClick={() => state.setQuery("")}
              aria-label={t("filters.reset")}
              className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-kj-sub hover:text-kj-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Формат тура. Сегменты, а не выпадающий список: вариантов три,
            и все три должны быть видны сразу. */}
        <div
          role="group"
          aria-label={t("filters.title")}
          className="flex flex-wrap gap-1.5 rounded-lg border border-kj-line bg-kj-dark/30 p-1"
        >
          {MODES.map((m) => {
            const active = state.mode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                aria-pressed={active}
                onClick={() => state.setMode(m.id)}
                className={`min-h-[36px] rounded-md px-3.5 py-1.5 text-xs sm:text-sm font-bold uppercase tracking-[0.08em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold ${
                  active
                    ? "bg-kj-gold text-kj-dark"
                    : "text-kj-sub hover:text-kj-ink"
                }`}
              >
                {t(m.key)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Счётчик — только когда фильтр реально что-то делает. */}
      {state.active && (
        <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-kj-line/60 pt-3 text-xs sm:text-sm text-kj-sub">
          <span aria-live="polite">
            {t("filters.found", { count: state.filtered.length })}
          </span>
          <button
            type="button"
            onClick={state.reset}
            className="font-bold uppercase tracking-[0.1em] text-kj-sky hover:text-kj-gold transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold"
          >
            {t("filters.reset")}
          </button>
        </p>
      )}
    </section>
  );
}
